'use client';

import { useState, useMemo } from 'react';
import {
  ExternalLink, ListFilter, ChevronLeft, ChevronRight,
  Plus, Swords, DoorOpen, Ban, Tag,
  ArrowDownToLine, ArrowUpFromLine, Coins, Receipt,
  Shield, ArrowUpRight, ArrowDownLeft,
} from 'lucide-react';
import { cn, truncateAddress, formatTimeAgo, formatUSD } from '@/lib/utils';
import { generateGridAvatarUrl } from '@/lib/avatar';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import type { V2GridSeatEvent, V2GridSeatEventType } from '@/lib/types/v2';
import { V2_CONSTANTS } from '@/lib/types/v2';

interface GridActivityTableProps {
  events: V2GridSeatEvent[];
}

type FilterKey = 'buyouts' | 'abandons' | 'forfeits' | 'yields' | 'deposits' | 'taxes';

const FILTER_TYPES: Record<FilterKey, V2GridSeatEventType[]> = {
  buyouts: ['BOUGHT_VACANT', 'BOUGHT_FROM_CREATOR', 'BOUGHT_OUT'],
  abandons: ['ABANDONED'],
  forfeits: ['FORFEITED'],
  yields: ['YIELD_CLAIMED'],
  deposits: ['DEPOSIT_ADDED', 'DEPOSIT_WITHDRAWN'],
  taxes: ['TAX_COLLECTED', 'PRICE_UPDATED'],
};

const FILTER_LABELS: Record<FilterKey, string> = {
  buyouts: 'Buyouts',
  abandons: 'Abandons',
  forfeits: 'Forfeits',
  yields: 'Yields',
  deposits: 'Deposits',
  taxes: 'Tax',
};

const EVENT_META: Record<string, { label: string; icon: typeof Plus; color: string }> = {
  BOUGHT_VACANT:       { label: 'Claimed',      icon: Plus,           color: 'text-emerald-400' },
  BOUGHT_FROM_CREATOR: { label: 'Buyout',        icon: Swords,         color: 'text-amber-400' },
  BOUGHT_OUT:          { label: 'Buyout',        icon: Swords,         color: 'text-amber-400' },
  ABANDONED:           { label: 'Abandoned',     icon: DoorOpen,       color: 'text-red-400' },
  FORFEITED:           { label: 'Forfeited',     icon: Ban,            color: 'text-red-400' },
  PRICE_UPDATED:       { label: 'Price Set',     icon: Tag,            color: 'text-purple-400' },
  DEPOSIT_ADDED:       { label: 'Deposit',       icon: ArrowDownToLine, color: 'text-emerald-400' },
  DEPOSIT_WITHDRAWN:   { label: 'Withdraw',      icon: ArrowUpFromLine, color: 'text-orange-400' },
  YIELD_CLAIMED:       { label: 'Yield',         icon: Coins,          color: 'text-emerald-400' },
  TAX_COLLECTED:       { label: 'Tax',           icon: Receipt,        color: 'text-muted-foreground' },
  ANTI_SNIPE_TRIGGERED:{ label: 'Anti-snipe',    icon: Shield,         color: 'text-blue-400' },
  MIGRATED_OUT:        { label: 'Migrated Out',  icon: ArrowUpRight,   color: 'text-muted-foreground' },
  MIGRATED_IN:         { label: 'Migrated In',   icon: ArrowDownLeft,  color: 'text-muted-foreground' },
};

const PAGE_SIZE = 20;

export function GridActivityTable({ events }: GridActivityTableProps) {
  const [activeFilters, setActiveFilters] = useState<Set<FilterKey>>(new Set());
  const [page, setPage] = useState(0);

  const toggleFilter = (key: FilterKey) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setPage(0);
  };

  const filteredEvents = useMemo(() => {
    if (activeFilters.size === 0) return events;
    const allowedTypes = new Set<V2GridSeatEventType>();
    for (const key of activeFilters) {
      for (const t of FILTER_TYPES[key]) allowedTypes.add(t);
    }
    return events.filter(e => allowedTypes.has(e.eventType));
  }, [events, activeFilters]);

  const totalPages = Math.max(1, Math.ceil(filteredEvents.length / PAGE_SIZE));
  const pagedEvents = filteredEvents.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const filterKeys = Object.keys(FILTER_TYPES) as FilterKey[];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold shrink-0">Activity</h3>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-7 gap-1.5 text-xs text-muted-foreground hover:text-foreground">
              <ListFilter className="h-3.5 w-3.5" />
              <span>Filter</span>
              {activeFilters.size > 0 && (
                <span className="ml-0.5 px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground text-[10px] font-medium leading-none">
                  {activeFilters.size}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40" onCloseAutoFocus={(e) => e.preventDefault()}>
            <DropdownMenuLabel className="text-xs">Filter by type</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {filterKeys.map((key) => (
              <DropdownMenuCheckboxItem
                key={key}
                checked={activeFilters.has(key)}
                onCheckedChange={() => toggleFilter(key)}
                onSelect={(e) => e.preventDefault()}
                className="text-xs"
              >
                {FILTER_LABELS[key]}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {filteredEvents.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">No activity yet</p>
      ) : (
        <div className="space-y-0.5">
          {pagedEvents.map((event) => {
            const meta = EVENT_META[event.eventType] || { label: event.eventType, icon: Plus, color: 'text-muted-foreground' };
            const Icon = meta.icon;
            const priceOrAmount = event.price || event.amount;
            const valueNum = priceOrAmount ? Number(priceOrAmount) / 10 ** V2_CONSTANTS.USDC_DECIMALS : 0;
            const seatNum = event.seatId ? event.seatId.split('-').pop() : undefined;
            const explorerBase = process.env.NEXT_PUBLIC_CHAIN_ID === '84532'
              ? 'https://sepolia.basescan.org'
              : 'https://basescan.org';

            return (
              <div
                key={event.id}
                className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted/30 transition-colors text-xs"
              >
                <Tooltip delayDuration={100}>
                  <TooltipTrigger asChild>
                    <span className={cn('shrink-0', meta.color)}>
                      <Icon className="h-3.5 w-3.5" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs px-2 py-1">{meta.label}</TooltipContent>
                </Tooltip>
                {seatNum !== undefined && (
                  <span className="text-muted-foreground w-6 shrink-0">#{seatNum}</span>
                )}
                <div className="flex items-center gap-1 min-w-0 flex-1">
                  <img
                    src={event.userIdentity?.avatarUrl || generateGridAvatarUrl(event.user, 12)}
                    alt=""
                    className="w-3.5 h-3.5 rounded-sm shrink-0"
                  />
                  <span className="text-muted-foreground truncate">
                    {event.userIdentity?.displayName || truncateAddress(event.user, 3)}
                  </span>
                </div>
                {valueNum > 0 && (
                  <span className="text-muted-foreground shrink-0">{formatUSD(valueNum)}</span>
                )}
                <span className="text-[10px] text-muted-foreground/60 shrink-0 w-10 text-right">
                  {formatTimeAgo(event.timestamp * 1000)}
                </span>
                <a
                  href={`${explorerBase}/tx/${event.txHash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground/40 hover:text-foreground shrink-0"
                >
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            );
          })}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-1.5 border-t border-border/30">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className={cn(
                  'p-1 rounded transition-colors',
                  page === 0 ? 'text-muted-foreground/30 cursor-not-allowed' : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
                )}
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>
              <span className="text-[10px] text-muted-foreground font-mono">
                {page + 1} / {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className={cn(
                  'p-1 rounded transition-colors',
                  page >= totalPages - 1 ? 'text-muted-foreground/30 cursor-not-allowed' : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
                )}
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
